import { useQuery, useSubscription } from '@apollo/client/react'
import { Center, FlexProps, Heading, HStack, Spinner, VStack } from '@chakra-ui/react'
import { ChatHeader, ChatHeaderProps } from '@src/components/sco/Chat/ChatHeader'
import { ChatMessageComposer } from '@src/components/sco/Chat/ChatMessageComposer'
import { ChatMessageList, ChatMessageListProps } from '@src/components/sco/Chat/ChatMessageList'
import { ChatThreadListItem } from '@src/components/sco/Chat/ChatThreadListItem'
import {
  canUnlockTeamAdvisorMessaging,
  isTeamAdvisorMessagingTarget,
} from '@src/components/sco/Chat/directMessagePaywall'
import { Paragraph } from '@src/components/sco/Paragraph'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'

export type ChatThreadProps = {
  attachmentId?: string
  attachmentType?: string
  boxProps?: Partial<FlexProps>
  bypassContentPermissions?: boolean
  chatHeaderProps?: Partial<ChatHeaderProps>
  chatMessageListProps?: Partial<ChatMessageListProps>
  hideComposer?: boolean
  hideHeader?: boolean
  enableFileAttachments?: boolean
  renderCustomHeader?: (threadId: string) => any
  threadId?: string
}

export const ChatThread = (props: ChatThreadProps) => {
  const { currentUser } = useCurrentUser()
  const threadSubscription = useSubscription(
    gql`
      subscription ($threadId: String!, $parentCommentId: String) {
        scoThreadComment(threadId: $threadId, parentCommentId: $parentCommentId) {
          id
          thread {
            id
            ${ChatThreadListItem.fragments.ScoThread()}
          }
        }
      }
    `,
    {
      variables: {
        threadId: props.threadId,
      },
    }
  )
  const query = useQuery(
    gql`
      query ($threadId: String) {
        chat {
          id
          thread(threadId: $threadId) {
            id
            threadId
            threadTypeKey
            userMemberships {
              id
              items {
                id
                userId
                user {
                  id
                  userId
                  isAdvisor
                  isToolUser
                  slug
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        threadId: props.threadId,
      },
    }
  )

  const thread = query.data?.chat?.thread
  const counterpartUsers =
    thread?.userMemberships?.items
      ?.filter(
        membership =>
          (thread.userMemberships?.items?.length || 0) === 1 ||
          membership.user?.userId !== currentUser?.userId
      )
      .map(membership => membership.user) || []
  const isTeamAdvisorThread =
    thread?.threadTypeKey === 'direct-chat' &&
    counterpartUsers.some(user => isTeamAdvisorMessagingTarget(user))
  const showTeamAdvisorPaywall = isTeamAdvisorThread && !canUnlockTeamAdvisorMessaging(currentUser)
  const showChatPaywall =
    !showTeamAdvisorPaywall &&
    thread?.threadTypeKey !== 'direct-chat' &&
    thread?.threadTypeKey !== 'advisor-session-chat' &&
    !currentUser?.contentPermissions?.canAccessChat &&
    !props.bypassContentPermissions

  const header = !props.hideHeader && (
    <>
      {props.renderCustomHeader ? (
        props.renderCustomHeader(props.threadId)
      ) : (
        <ChatHeader {...props.chatHeaderProps} boxProps={props.boxProps} threadId={props.threadId} />
      )}
    </>
  )

  if (query.error) {
    return (
      <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
        {header}
        <Center padding={'1em'} width={'full'}>
          <Paragraph textAlign={'center'}>
            {'Error: '}
            {query.error.toString()}
          </Paragraph>
        </Center>
      </VStack>
    )
  }

  if (!thread) {
    return (
      <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
        {header}
        <Center flexGrow={1} padding={'1em'} width={'full'}>
          <Spinner />
        </Center>
      </VStack>
    )
  }

  return (
    <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
      {header}
      <ChatMessageList
        key={`thread-${props.threadId}`}
        inlineReplies={true}
        threadId={props.threadId}
        {...props.chatMessageListProps}
      />
      {!props.hideComposer && (
        <>
          {showTeamAdvisorPaywall ? (
            <VStack align={'center'} bgColor={'colorPalette.container'} justify={'center'} padding={'2em'}>
              <Heading>Upgrade to message the Startups.com advisor team.</Heading>
              <HStack align={'center'} justify={'center'} paddingY={'1em'}>
                <PaywalledAction
                  contentPermission={'canAccessAdvisorTeam'}
                  upgradeCtaLabel={'Upgrade to message'}
                  variant={'button-only'}
                />
              </HStack>
            </VStack>
          ) : showChatPaywall ? (
            <VStack align={'center'} bgColor={'colorPalette.container'} justify={'center'} padding={'2em'}>
              <Heading>Ready to join the conversation?</Heading>
              <HStack align={'center'} justify={'center'} paddingY={'1em'}>
                <PaywalledAction
                  contentPermission={'canAccessChat'}
                  intent={'member-message'}
                  variant={'button-only'}
                />
              </HStack>
            </VStack>
          ) : (
            <ChatMessageComposer
              newCommentValues={{ threadId: props.threadId }}
              placeholder={'Type a message...'}
              enableFileAttachments={
                props.enableFileAttachments ?? thread.threadTypeKey !== 'advisor-session-chat'
              }
            />
          )}
        </>
      )}
    </VStack>
  )
}
